import { useState } from "react";
import { useCardExpandContext } from "../../context/DashCardExpandContext";
import Drafts from "./ExpandedTabs/Drafts";
import Quotes from "./ExpandedTabs/Quotes";
import History from "./ExpandedTabs/History";
import OngoingOrders from "./ExpandedTabs/OngoingOrders";
import RFQs from "./ExpandedTabs/RFQs";
import { LogisticsRequestQuote } from "../../types/PartnerCompanyDetails";

const tabs = [
  // "Drafts",
  "RFQs",
  "Quotes",
  "Ongoing Orders",
  "History",
];

type TabItem = LogisticsRequestQuote;

export default function ServicesDetailsTab() {
  const [activeTab, setActiveTab] = useState("RFQs");
  const [selectedItemId, setSelectedItemId] = useState<TabItem["id"] | null>(
    null,
  );

  const { isCardExpanded, toggleExpand } = useCardExpandContext();

  // 🧩 Handle selecting an item
  const handleSelectItem = (item: TabItem) => {
    setSelectedItemId(item.id);
    toggleExpand(); // open expanded view
  };

  // 🧭 Handle closing expanded view
  const handleCloseExpand = () => {
    setSelectedItemId(null);
    toggleExpand(); // collapse
  };

  // 🧱 Render expanded or tab content
  if (isCardExpanded && selectedItemId) {
    return (
      <ExpandedView
        currentTab={activeTab}
        selectedQuoteId={selectedItemId}
        onClose={handleCloseExpand}
      />
    );
  }

  return (
    <section id="transactions-info" className="card h-full">
      {/* Tabs */}
      <div className="flex mb-3 justify-between text-gray-500">
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-2 py-2 text-sm rounded-lg ${
              activeTab === tab
                ? "bg-gray-200 font-medium text-black"
                : "border-transparent"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="space-y-4 h-[420px] overflow-y-auto hide-scrollbar">
        <TabContent currentTab={activeTab} onSelectItem={handleSelectItem} />
      </div>
    </section>
  );
}

/* 🧩 Tab Content Renderer */
function TabContent({
  currentTab,

  onSelectItem,
}: {
  currentTab: string;

  onSelectItem: (item: any) => void;
}) {
  switch (currentTab) {
    case "Drafts":
      return <Drafts onSelect={onSelectItem} />;
    case "RFQs":
      return <RFQs onSelect={onSelectItem} />;
    case "Quotes":
      return <Quotes onSelect={onSelectItem} />;
    case "Ongoing Orders":
      return <OngoingOrders onSelect={onSelectItem} />;
    case "History":
      return <History onSelect={onSelectItem} />;
    default:
      return (
        <p className="text-center text-sm text-gray-400">
          🏢 No partner companies found
        </p>
      );
  }
}

/* 🧱 Expanded View Renderer */
function ExpandedView({
  currentTab,
  selectedQuoteId,
  onClose,
}: {
  currentTab: string;
  selectedQuoteId: TabItem["id"];
  onClose: () => void;
}) {
  const map: Record<string, JSX.Element> = {
    Drafts: <Drafts.Expanded item={selectedQuoteId} onClose={onClose} />,
    RFQs: <RFQs.Expanded selectedQuoteId={selectedQuoteId} onClose={onClose} />,
    Quotes: (
      <Quotes.Expanded selectedQuoteId={selectedQuoteId} onClose={onClose} />
    ),
    "Ongoing Orders": (
      <OngoingOrders.Expanded
        selectedQuoteId={selectedQuoteId}
        onClose={onClose}
      />
    ),
    History: (
      <History.Expanded selectedQuoteId={selectedQuoteId} onClose={onClose} />
    ),
  };

  return map[currentTab] || null;
}
